import {
  CAPTCHA_EVENTS,
  CAPTCHA_FAILURE_CODES,
  CaptchaUnsolvedError,
} from 'helpers/captcha/contracts'
import type {
  CaptchaChallenge,
  CaptchaCoordinatorOptions,
  CaptchaEventName,
  CaptchaIssue,
  CaptchaProof,
  CaptchaSolveResult,
  CaptchaTelemetry,
  CaptchaTelemetryEvent,
} from 'helpers/captcha/contracts'
import {
  isCaptchaRequiredError,
  isCaptchaVerificationRejectedError,
  parseCaptchaChallenge,
} from 'helpers/captcha/parser'
import { captchaProofHeaders } from 'helpers/captcha/proof'
import {
  captchaErrorCodeOf,
  captchaTelemetry,
} from 'helpers/captcha/telemetry'

const SOLVER_ERROR_CODE = 'solver_error'

function track(
  telemetry: CaptchaTelemetry,
  event: CaptchaTelemetryEvent,
): void {
  try {
    void Promise.resolve(telemetry.track(event)).catch(
      () => undefined,
    )
  } catch {
    // Telemetry must not alter challenge behavior.
  }
}

function reportUnexpected(
  telemetry: CaptchaTelemetry,
  issue: CaptchaIssue,
): void {
  try {
    void Promise.resolve(telemetry.reportUnexpected(issue)).catch(
      () => undefined,
    )
  } catch {
    // Telemetry must not alter challenge behavior.
  }
}

function cancellation(): CaptchaUnsolvedError {
  return new CaptchaUnsolvedError({ outcome: 'cancelled' })
}

function solveEventName(
  result: CaptchaSolveResult,
): CaptchaEventName {
  if (result.outcome === 'solved') return CAPTCHA_EVENTS.solved
  if (result.outcome === 'cancelled') return CAPTCHA_EVENTS.cancelled
  return CAPTCHA_EVENTS.clientError
}

/**
 * Runs a protected request and resolves any CAPTCHA the backend demands.
 *
 * The request goes out bare and a 403 CAPTCHA_REQUIRED is solved, then the
 * request is resent with the proof header. With proactive settings the
 * challenge is solved first and the proof rides on the first request. Each
 * token the backend rejects reopens the challenge until it is solved again
 * or dismissed.
 *
 * Rejects with CaptchaUnsolvedError when the challenge is cancelled or cannot
 * be solved; once `isCurrent` turns false every pending step rejects as a
 * cancellation instead of sending.
 */
export async function runWithCaptcha<T>(
  options: CaptchaCoordinatorOptions<T>,
): Promise<T> {
  const telemetry = options.telemetry ?? captchaTelemetry
  const isCurrent = options.isCurrent ?? (() => true)
  const { operation, request, solve, settings } = options

  const ensureCurrent = () => {
    if (!isCurrent()) throw cancellation()
  }

  const solveTracked = async (
    challenge: CaptchaChallenge,
    afterRejection = false,
  ): Promise<CaptchaSolveResult> => {
    const startedAt = Date.now()
    track(telemetry, {
      name: CAPTCHA_EVENTS.opened,
      operation,
      challenge,
    })
    let result: CaptchaSolveResult

    try {
      result = await solve(challenge, {
        onWidgetError: (errorCode) =>
          reportUnexpected(telemetry, {
            operation,
            phase: 'solve',
            errorCode,
            challenge,
          }),
        ...(options.action !== undefined
          ? { action: options.action }
          : {}),
        ...(options.cData !== undefined
          ? { cData: options.cData }
          : {}),
        afterRejection,
      })
    } catch {
      result = { outcome: 'failed', code: SOLVER_ERROR_CODE }
    }

    const errorCode =
      result.outcome === 'solved'
        ? undefined
        : captchaErrorCodeOf(result)
    track(telemetry, {
      name: solveEventName(result),
      operation,
      challenge,
      result,
      errorCode,
      elapsedMs: Date.now() - startedAt,
    })

    if (
      result.outcome === 'unsupported_mode' ||
      result.outcome === 'failed'
    ) {
      reportUnexpected(telemetry, {
        operation,
        phase:
          result.outcome === 'unsupported_mode' ? 'parse' : 'render',
        errorCode: captchaErrorCodeOf(result),
        challenge,
      })
    }

    return result
  }

  // Each token the backend rejects reopens the challenge on its failure state
  // until the user solves again or leaves it.
  const sendUntilAccepted = async (
    challenge: CaptchaChallenge,
    proof: CaptchaProof,
  ): Promise<T> => {
    let currentProof = proof
    for (;;) {
      try {
        const outcome = await request(
          captchaProofHeaders(currentProof),
        )
        ensureCurrent()
        return outcome
      } catch (error) {
        if (!isCurrent()) throw cancellation()
        if (!isCaptchaVerificationRejectedError(error)) throw error

        reportUnexpected(telemetry, {
          operation,
          phase: 'resubmit',
          errorCode: CAPTCHA_FAILURE_CODES.verificationRejected,
          challenge,
        })
        const retry = await solveTracked(challenge, true)
        ensureCurrent()
        if (retry.outcome !== 'solved') {
          throw new CaptchaUnsolvedError(retry)
        }
        currentProof = retry.proof
      }
    }
  }

  let challenge: CaptchaChallenge
  try {
    if (!settings.proactiveEnabled) {
      const outcome = await request()
      ensureCurrent()
      return outcome
    }

    const proactiveChallenge: CaptchaChallenge = {
      provider: 'turnstile',
      mode: 'managed',
      siteKey: settings.siteKey,
    }
    const proactiveResult = await solveTracked(proactiveChallenge)
    ensureCurrent()
    if (proactiveResult.outcome !== 'solved') {
      throw new CaptchaUnsolvedError(proactiveResult)
    }
    return await sendUntilAccepted(
      proactiveChallenge,
      proactiveResult.proof,
    )
  } catch (error) {
    if (!isCurrent()) throw cancellation()
    if (!isCaptchaRequiredError(error)) throw error

    const parsedChallenge = parseCaptchaChallenge(
      error,
      settings.siteKey,
    )
    if (!parsedChallenge) {
      reportUnexpected(telemetry, {
        operation,
        phase: 'parse',
        errorCode: CAPTCHA_FAILURE_CODES.malformedChallenge,
      })
      throw error
    }
    challenge = parsedChallenge
  }

  track(telemetry, {
    name: CAPTCHA_EVENTS.required,
    operation,
    challenge,
  })
  const result = await solveTracked(challenge)
  ensureCurrent()
  if (result.outcome !== 'solved')
    throw new CaptchaUnsolvedError(result)

  try {
    const outcome = await sendUntilAccepted(challenge, result.proof)
    track(telemetry, {
      name: CAPTCHA_EVENTS.resubmitSuccess,
      operation,
      challenge,
    })
    return outcome
  } catch (error) {
    if (!isCurrent()) throw cancellation()
    if (!isCaptchaRequiredError(error)) throw error

    const errorCode = CAPTCHA_FAILURE_CODES.repeatedChallenge
    track(telemetry, {
      name: CAPTCHA_EVENTS.resubmitFailure,
      operation,
      challenge,
      result: { outcome: 'failed', code: errorCode },
      errorCode,
    })
    reportUnexpected(telemetry, {
      operation,
      phase: 'resubmit',
      errorCode,
      challenge,
    })
    throw new CaptchaUnsolvedError({
      outcome: 'failed',
      code: errorCode,
    })
  }
}
